import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  forwardRef,
  Ref,
  useImperativeHandle,
  useCallback,
} from "react";

import moment, { Moment } from "moment";
import { Calendar } from "@natscale/react-calendar";
import { FormattedMessage } from "react-intl";

// Interface
import { timeSlotsType } from "./SelectDateInterface";

// CSS
import "./calendar.scss";
import { Dimmer, Loader } from "semantic-ui-react";

// Types
type AppCalendarProps = {
  // data
  value?: Moment | null;
  defaultValue?: string | Moment;
  calendarList?: {
    active: Date[];
    inactive: Date[];
  };
  timeslotList?: timeSlotsType[];
  isLoadingCalendar?: boolean;
  isLoadingNextMonth?: boolean;
  className?: string;
  language?: "en-US" | "th-TH";

  // callback
  onChange?: (date: Moment | null) => any;
  onNextMonth?: (dateRange: any[]) => Promise<any>;
  onGetCalendar?: (dateRange: any[], isTelemed: boolean) => any;
  onLoad?: () => any;
};

type GetCalendarParams = {
  isTelemed: boolean;
  appointmentType?: "check_up" | "specialty";
  startAppointmentDate?: string;
};

export type AppCalendarRef = {
  getCalendar: (params: GetCalendarParams) => any;
  updateYearMonthList: (viewDate?: Moment | null) => any;
};

// Const
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTHS_TH = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const monthsLabel = {
  0: "มกราคม",
  1: "กุมภาพันธ์",
  2: "มีนาคม",
  3: "เมษายน",
  4: "พฤษภาคม",
  5: "มิถุนายน",
  6: "กรกฎาคม",
  7: "สิงหาคม",
  8: "กันยายน",
  9: "ตุลาคม",
  10: "พฤศจิกายน",
  11: "ธันวาคม",
};

const weekDaysLabel = {
  0: "อา",
  1: "จ",
  2: "อ",
  3: "พ",
  4: "พฤ",
  5: "ศ",
  6: "ส",
};

export const BE_FORMAT_DATE = "YYYY-MM-DD";
export const AD_FORMAT_DATE = "YYYY-MM-DD";

const AppCalendar = (props: AppCalendarProps, ref: Ref<any>) => {
  // Loading
  const [isReloadCalendar, setIsReloadCalendar] = useState(false);
  // dat
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  // Ref
  const yearMonthList = useRef<string[]>([]);
  const initialViewDate = useRef<any>(null);
  const isMounted = useRef<boolean>(false);
  const isDefaultValue = useRef<boolean>(false);
  const isUpdatingRef = useRef(false)

  useImperativeHandle<AppCalendarRef, AppCalendarRef>(ref, () => ({
    getCalendar: handleGetCalendar,
    updateYearMonthList: handleUpdateYearMonthList,
    // clearYearMonthList: handleClearYearMonthList,
    // clearInitialViewDate: handleClearInitialViewDate,
  }));

  // Callback Effect
  const findDate = useCallback(
    (value: Moment | null) => {
      return props.calendarList?.active?.find(
        (date: Date) =>
          moment(date).format(BE_FORMAT_DATE) === value?.format(BE_FORMAT_DATE)
      );
    },
    [props.calendarList?.active]
  );

  const getDefaultDate = useCallback(() => {
    const currentDate = moment();
    const currentMonth = currentDate.month();

    // Set the default value based on conditions
    let defaultValue = moment(props.defaultValue || undefined);

    if (defaultValue.isBefore(currentDate)) {
      defaultValue = currentDate;
    } else if (defaultValue.month() === currentMonth) {
      defaultValue = currentDate;
    } else if (defaultValue.month() > currentMonth) {
      defaultValue = moment().startOf("month");
    }

    return defaultValue;
  }, [props.defaultValue]);

  // Effect
  useEffect(() => {
    props.onLoad?.();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth - 15,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  useEffect(() => {
    const active = findDate(props.value || null);
    const defaultDate = findDate(
      props.defaultValue ? moment(props.defaultValue) : null
    );

    if (defaultDate && !isDefaultValue.current) {
      props.onChange?.(moment(defaultDate));
      isDefaultValue.current = true;
    } else if (!active && props.value) {
      props.onChange?.(null);
    }
  }, [props.calendarList?.active, props.timeslotList, props.value]);

  useEffect(() => {
    // selected date manual
    handleSetSelectedDateManual();
  }, [isReloadCalendar, props.value]);

  useEffect(() => {
    setIsReloadCalendar(true);

    setTimeout(() => {
      setIsReloadCalendar(false);
    });
  }, [props.isLoadingCalendar]);

  const isDateBE = useMemo(() => {
    return props.language === "th-TH" ? true : false;
  }, [props.language]);

  const mouth = useMemo(() => {
    return isDateBE ? monthsLabel : undefined;
  }, []);

  const weekDay = useMemo(() => {
    return isDateBE ? weekDaysLabel : undefined;
  }, []);

  const isValue = useMemo(() => {
    return isDateBE
      ? new Date(moment(props.value).format(BE_FORMAT_DATE))
      : undefined;
  }, [isDateBE, props.value]);

  // Memo
  const date = useMemo(() => {
    return props.value ? props.value.toDate() : undefined;
  }, [props.value]);

  useEffect(() => {
    const target = document.querySelector(".rc_header_label") as HTMLDivElement;

    const config: MutationObserverInit = {
      characterData: true,
      childList: true,
      subtree: true,
    };

    const handleChange = () => {
      if (isUpdatingRef.current) {
        isUpdatingRef.current = false;

        return;
      }

      const convertToBEDate = (
        spans: (HTMLSpanElement | ChildNode | HTMLButtonElement | null)[],
        format?: (value:string) => string
      ) => {
        if (isDateBE) {
          isUpdatingRef.current = true;

          for (const span of spans) {
            if (span) {
              let textContent = span.textContent || "";

              if ("dataset" in span) {
                textContent = span.dataset?.original || textContent;
                textContent =  format? format(textContent) : textContent;
                span.dataset.original = textContent
              }

              span.textContent = String(Number(textContent) + 543);
            }
          }
        }
      };

      if (target.className.includes("rc_header_label-years")) {
        const spanElement = target.querySelector("span");

        const [startYear, , endYear] = Array.from(
          spanElement?.childNodes || []
        );

        convertToBEDate([startYear, endYear]);

        const buttons = document.querySelectorAll(
          ".rc_body .rc_body-cell button"
        );

        convertToBEDate(Array.from(buttons) as HTMLButtonElement[]);

        return;
      }

      if (target.className.includes("rc_header_label-days-of-month")) {
        const spanElement = target.querySelectorAll("span");
      
        const month = ((isDateBE ? MONTHS_TH : MONTHS).indexOf(spanElement[0].textContent || "") + 1)
        
        convertToBEDate([spanElement[1]],(value: string) => {
            const prevMonth = Number(spanElement[0].dataset.month)
            if (prevMonth === 12 &&  month === 1) {
                return String(Number(value)+1)
            } else if (prevMonth === 1 && month === 12) {
              return String(Number(value)-1)
            } 
            return value;
        });
       console.log("saika ~ spanElement:", spanElement[0].dataset.month)
       console.log("saika ~ month:", month)
        if (spanElement[0]) {
          spanElement[0].dataset.month =`${month}`
        }

      } else if (target.className.includes("rc_header_label-months")) {
        const spanElement = target.querySelector("span");

        convertToBEDate([spanElement]);
      }
    };

    handleChange();

    isUpdatingRef.current = false;

    const observer = new MutationObserver(handleChange);

    observer.observe(target, config);

    return () =>  observer.disconnect();
  }, [isDateBE , isReloadCalendar]);

  // Handler

  const handleUpdateYearMonthList = (viewDate?: Moment | null) => {
    const calendarAll = [
      ...(props.calendarList?.active || []),
      ...(props.calendarList?.inactive || []),
    ]
      .sort((a: Date, b: Date) =>
        ("" + a.toISOString()).localeCompare(b.toISOString())
      )
      .map((date) => moment(date).format("YYYY-MM"));
    const uniqueDate = Array.from(new Set(calendarAll));
    // -uniqueDate.pop();

    yearMonthList.current = uniqueDate;

    setIsReloadCalendar(true);

    viewDate = viewDate || props.value;

    if (viewDate) {
      initialViewDate.current = new Date(
        moment(viewDate).format(BE_FORMAT_DATE)
      );
    }

    setTimeout(() => {
      setIsReloadCalendar(false);
    });
  };

  const handleClearYearMonthList = () => {
    yearMonthList.current = [];
  };

  const handleClearInitialViewDate = () => {
    initialViewDate.current = null;
  };

  const getYearMonthCalendar = () => {
    const headerDate = document.querySelectorAll(
      ".rc_header_label.rc_header_label-days-of-month span"
    );
    const [elmMonth, elmYear] = Array.from(headerDate);
    let getYearMonth: string = "";
    if (elmMonth && elmYear) {
      const year = elmYear.dataset.original || elmYear.textContent || "";

      getYearMonth = `${year}-${(
        (isDateBE ? MONTHS_TH : MONTHS).indexOf(elmMonth.textContent || "") + 1
      )
        .toString()
        .padStart(2, "0")}`;
    }
    return getYearMonth;
  };

  const handleGetNextMonth = async (value: string) => {
    const start_date = moment(`${value}-01`);
    const halfMonth = Math.ceil(start_date.daysInMonth() / 2);
    const date = moment(`${start_date.format("YYYY-MM")}-${halfMonth}`);

    const find = [
      ...(props.calendarList?.active || []),
      ...(props.calendarList?.inactive || []),
    ]?.find((value: Date) => value.toISOString() === date.toISOString());

    let dateRange: any[] = [];

    const endOfMonth = date.clone().endOf("month");
    const nextMonth = endOfMonth.clone().add(1, "days");
    const halfNextMonth = Math.ceil(nextMonth.daysInMonth() / 2);

    if (find) {
      dateRange = [
        {
          start_date: date.add(1, "days").format(BE_FORMAT_DATE),
          end_date: endOfMonth.format(BE_FORMAT_DATE),
        },
        {
          start_date: nextMonth.format(BE_FORMAT_DATE),
          end_date: `${nextMonth.format("YYYY-MM")}-${halfNextMonth}`,
        },
      ];
    } else {
      dateRange = [
        {
          start_date: start_date.format(BE_FORMAT_DATE),
          end_date: date.format(BE_FORMAT_DATE),
        },
        {
          start_date: date.add(1, "days").format(BE_FORMAT_DATE),
          end_date: endOfMonth.format(BE_FORMAT_DATE),
        },
        {
          start_date: nextMonth.format(BE_FORMAT_DATE),
          end_date: `${nextMonth.format("YYYY-MM")}-${halfNextMonth}`,
        },
      ];
    }

    initialViewDate.current = new Date(dateRange[0].start_date);

    await props.onNextMonth?.(dateRange);

    setIsReloadCalendar(true);
    setTimeout(() => {
      setIsReloadCalendar(false);
    });
  };

  const handleSetSelectedDateManual = () => {
    if (props.value) {
      const yearMonth = getYearMonthCalendar();
      const nextYearMonth = moment(`${yearMonth}-01`)
        .add(1, "months")
        .format("YYYY-MM");
      const selectDate = moment(props.value).format(BE_FORMAT_DATE);

      for (const elm of Array.from(
        document.querySelectorAll(".rc .rc_body-cell")
      )) {
        const btn = elm.querySelector("button");
        if (elm.className.includes("rc_selected")) {
          elm.classList.remove("rc_selected", "rc_disabled");
        }
        if (
          elm.className.includes("rc_active") &&
          `${yearMonth}-${btn?.textContent?.padStart(2, "0")}` === selectDate &&
          !elm.className.includes("rc_highlight")
        ) {
          elm.classList.add("rc_selected");
        } else if (
          (elm.className.includes("rc_lr") ||
            elm.className.includes("rc_lc")) &&
          `${nextYearMonth}-${btn?.textContent?.padStart(2, "0")}` ===
            selectDate &&
          !elm.className.includes("rc_active")
        ) {
          elm.classList.add("rc_selected", "rc_disabled");
        }
      }
    }
  };

  const handleIsDisabled = (date: Date) => {
    setTimeout(() => {
      const yearMonth = getYearMonthCalendar();
      const first = yearMonthList.current[0];

      // -const prevMonth = isMounted.current && first > yearMonth;

      if (!yearMonthList.current.includes(yearMonth) && yearMonth > first) {
        yearMonthList.current = [...yearMonthList.current, yearMonth];

        handleGetNextMonth(yearMonth);
      } else if (yearMonthList.current.includes(yearMonth)) {
        handleSetSelectedDateManual();
      }

      if (first === yearMonth && !isMounted.current) {
        isMounted.current = true;
      }
    });

    return !props.calendarList?.active.find(
      (item: Date) => item.toISOString() === date.toISOString()
    );
  };

  const handleGetCalendar = async (params: GetCalendarParams) => {
    handleClearYearMonthList();
    handleClearInitialViewDate();

    const defaultDate = getDefaultDate();

    const initDate = !isMounted.current ? defaultDate ?? undefined : undefined;
    const currentDate = moment(initDate);

    let hour = 0;

    //  วันที่นัดหมายล่วงหน้า (เมื่อไม่ไช่เป็นนัดหมายของ ecoupon)
    if (params.appointmentType === "check_up") {
      // -hour = 24;
    }

    if (params.appointmentType === "specialty") {
      if (params.isTelemed) {
        hour = 1;
      } else {
        hour = 4;
      }
    }

    // -if (props.defaultValue) {
    //   hour = 0;
    // }

    let start_date = currentDate.clone().add(hour, "hours");

    // implement diag form appointment calculated here
    if (params.startAppointmentDate) {
      start_date = moment(params.startAppointmentDate, "DD/MM/YYYY");
      console.log("getCalendars by appointment set", start_date);

      if (start_date.diff(moment(), "days") < 3) {
        start_date = moment().add(3, "days");
      }

      console.log("getCalendars by check date > 3", start_date);
    }

    const halfMonth = Math.ceil(start_date.daysInMonth() / 2);
    let dateRange: any[] = [];

    if (+start_date.format("DD") < halfMonth) {
      const end_date = `${start_date.format("YYYY-MM")}-${halfMonth}`;

      dateRange.push({
        start_date: start_date.format(BE_FORMAT_DATE),
        end_date,
      });
      yearMonthList.current = [start_date.format("YYYY-MM")];
      start_date = moment(end_date).add(1, "days");
    } else {
      yearMonthList.current = [start_date.format("YYYY-MM")];
    }

    const endOfMonth = start_date.clone().endOf("month");
    const nextMonth = endOfMonth.clone().add(1, "days");
    const halfNextMonth = Math.ceil(nextMonth.daysInMonth() / 2);

    dateRange = [
      ...dateRange,
      {
        start_date: start_date.format(BE_FORMAT_DATE),
        end_date: endOfMonth.format(BE_FORMAT_DATE),
      },
      {
        start_date: nextMonth.format(BE_FORMAT_DATE),
        end_date: `${nextMonth.format("YYYY-MM")}-${halfNextMonth}`,
      },
    ];

    props.onGetCalendar?.(dateRange, params.isTelemed);
  };

  const handleDateChange = (value: Date) => {
    const yearMonth = getYearMonthCalendar();

    const filterActiveInMonth = props.calendarList?.active.filter(
      (item: Date) => {
        let month = `${item.getMonth() + 1}`;
        if (month.length < 2) {
          month = "0" + month;
        }
        
        return `${item.getFullYear()}-${month}`.includes(`${yearMonth}`);
      }
    );

    if (
      (filterActiveInMonth || []).some(
        (item: Date) => item.toISOString() === value.toISOString()
      )
    ) {
      props.onChange?.(moment(value));
    } else {
      console.log("out of array active can't select");
    }
  };

  return (
    <div
      className={props.className}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <Dimmer
        active={props.isLoadingNextMonth || props.isLoadingCalendar}
        inverted
      >
        <Loader inverted>
          <FormattedMessage id="bplusClinicKey297" />
        </Loader>
      </Dimmer>
      <div style={{ display: "flex", justifyContent: "center" }}>
        {useMemo(
          () =>
            (props.calendarList?.active[0] ||
              props.calendarList?.inactive[0]) &&
            !isReloadCalendar ? (
              <Calendar
                className="calendar-custom"
                // value={date}
                // @ts-ignore
                weekDaysLabel={weekDay}
                monthsLabel={mouth}
                highlights={props.calendarList?.inactive}
                onChange={handleDateChange}
                disablePast={true}
                // disableToday={true}
                // disableFuture={true}
                // value={isValue}
                isDisabled={handleIsDisabled}
                startOfWeek={0}
                size={dimensions.width}
                initialViewDate={
                  initialViewDate.current || props.calendarList?.active?.[0]
                }
                // minAllowedDate={props.calendarList.active?.[0]}
                // maxAllowedDate={maxAllowedDate}
              />
            ) : (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Calendar
                  weekDaysLabel={weekDay}
                  monthsLabel={mouth}
                  className="calendar-custom"
                  initialViewDate={initialViewDate.current}
                  startOfWeek={0}
                  size={dimensions.width}
                  isDisabled={handleIsDisabled}
                  // @ts-ignore
                  disablePast={true}
                  disableToday={true}
                  disableFuture={true}
                />
              </div>
            ),
          [props.calendarList, date, dimensions, isReloadCalendar]
        )}
      </div>
    </div>
  );
};

const ForwardedAppCalendar = forwardRef<any, AppCalendarProps>(AppCalendar);

export default React.memo(ForwardedAppCalendar);
